import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { setUserPersonalManager } from '@/services/personalManagerService'
import {
  setUserPedagogyLeadRole,
  setUserPedagogySpecialistRole,
} from '@/services/pedagogySpecialistService'
import { setUserTerritorialManager } from '@/services/territorialManagerService'
import type { ManagementJobTitle, RoyaltyRole } from '@/types'

/**
 * Роли сотрудника УК на экране `/management/users`.
 *
 * Три независимые оси, у каждой свой серверный гейт — здесь они только собраны в одном
 * месте, правило доступа не дублируется:
 *   1. должность (`management_company_access.job_title`) — ТУ / ЛМ / педагоги / юрист;
 *   2. роль в контуре роялти (`royalty_role`) — руководитель / бухгалтер / админ;
 *   3. app-роль супер-админа (`is_super_admin`).
 *
 * Вызовы RPC для ТУ, ЛМ и педагогов берутся из их собственных сервисов: второй
 * реализации этих ручек не заводим.
 */

type RpcResult<T> = { data: T | null; error: unknown }
type RpcClient = {
  rpc: <T>(functionName: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<T>>
}

function rpcClient(): RpcClient {
  return getSupabase() as unknown as RpcClient
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

/** Должность, которую экран умеет выдавать и снимать. `other` в этот список не входит. */
export type AssignableJobTitle =
  | 'territorial_manager'
  | 'personal_manager'
  | 'pedagogy_specialist'
  | 'pedagogy_lead'
  | 'lawyer'

export const JOB_TITLE_LABELS: Record<Exclude<ManagementJobTitle, null>, string> = {
  territorial_manager: 'Территориальный управляющий',
  personal_manager: 'Личный менеджер',
  pedagogy_specialist: 'Специалист по педагогам',
  pedagogy_lead: 'Руководитель педагогов',
  lawyer: 'Юрист',
  other: 'Другое',
}

export const ASSIGNABLE_JOB_TITLES: AssignableJobTitle[] = [
  'territorial_manager',
  'personal_manager',
  'pedagogy_specialist',
  'pedagogy_lead',
  'lawyer',
]

export const ROYALTY_ROLE_LABELS: Record<RoyaltyRole, string> = {
  territorial_manager: 'Территориальный управляющий',
  head: 'Руководитель',
  accountant: 'Бухгалтер',
  super_admin: 'Администратор',
}

/** Роли, которые принимает `set_management_company_royalty_role` (ТУ в её списке нет). */
export const ASSIGNABLE_ROYALTY_ROLES: RoyaltyRole[] = ['head', 'accountant', 'super_admin']

export const ROYALTY_ROLE_HINTS: Record<RoyaltyRole, string> = {
  territorial_manager: 'Только просмотр закреплённых франчайзи. Через этот экран не выдаётся.',
  head: 'Начисления и требования на оплату; может менять роли в роялти. Разносить платежи, фиксировать период и списывать долг — нет.',
  accountant: 'Начисления, платежи, требования и фиксация периода. Роли в роялти не выдаёт.',
  super_admin: 'Всё, что может бухгалтер, плюс админ-права контура и выдача ролей в роялти.',
}

/**
 * Почему должность нельзя выдать этому человеку. `null` — можно.
 *
 * Зеркало серверных гардов (проверены по живым телам функций на PROD 02.09.2026):
 * должность в `management_company_access` одна на человека, поэтому чужую нельзя
 * затирать молча; ЛМ и педагоги — только `standard`; ТУ — любой уровень.
 */
export function jobTitleBlockReason(
  title: AssignableJobTitle,
  target: { mc_is_active: boolean; access_level: string | null; job_title: ManagementJobTitle }
): string | null {
  if (!target.mc_is_active || target.access_level === null) {
    return 'Нет активного доступа в УК — сначала выдайте доступ.'
  }
  if (target.job_title === title) return 'Должность уже выдана.'
  if (target.job_title !== null && target.job_title !== 'other') {
    // ЛМ и педагоги пускают 'other', остальные — нет; но менять должность в один шаг
    // всё равно нельзя: сначала снимаем текущую, иначе снятие прав пройдёт незаметно.
    return `Сначала снимите текущую должность «${JOB_TITLE_LABELS[target.job_title]}».`
  }
  if (title === 'personal_manager' && target.access_level !== 'standard') {
    return 'Личным менеджером может быть только сотрудник с обычным доступом УК.'
  }
  if ((title === 'pedagogy_specialist' || title === 'pedagogy_lead') && target.access_level !== 'standard') {
    return 'Педагогические роли выдаются только сотрудникам с обычным доступом УК.'
  }
  return null
}

/** Юрист — единственная должность без своего сервиса, ручка заведена миграцией 20260901140000. */
export async function setUserLawyer(params: { userId: string; isLawyer: boolean }): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    rpcClient().rpc<null>('set_user_lawyer', {
      p_user_id: params.userId,
      p_is_lawyer: params.isLawyer,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function assignJobTitle(params: {
  userId: string
  title: AssignableJobTitle
  note?: string | null
}): Promise<void> {
  const { userId, title, note } = params
  switch (title) {
    case 'territorial_manager':
      return setUserTerritorialManager({ userId, isTm: true, note: note ?? null })
    case 'personal_manager':
      return setUserPersonalManager({ userId, isPm: true, note: note ?? null })
    case 'pedagogy_specialist':
      return setUserPedagogySpecialistRole({ userId, isSpecialist: true, note: note ?? null })
    case 'pedagogy_lead':
      return setUserPedagogyLeadRole({ userId, isLead: true, note: note ?? null })
    case 'lawyer':
      return setUserLawyer({ userId, isLawyer: true })
  }
}

/**
 * Снятие идёт ТОЙ ЖЕ ручкой, что выдача: у ТУ она закрывает назначения франчайзи,
 * у педагогов — отказывается снимать роль при живых закреплениях. Обнулить
 * `job_title` чужой ручкой значило бы обойти эти гарды.
 */
export async function clearJobTitle(params: {
  userId: string
  current: AssignableJobTitle
  note?: string | null
}): Promise<void> {
  const { userId, current, note } = params
  switch (current) {
    case 'territorial_manager':
      return setUserTerritorialManager({ userId, isTm: false, note: note ?? null })
    case 'personal_manager':
      return setUserPersonalManager({ userId, isPm: false, note: note ?? null })
    case 'pedagogy_specialist':
      return setUserPedagogySpecialistRole({ userId, isSpecialist: false, note: note ?? null })
    case 'pedagogy_lead':
      return setUserPedagogyLeadRole({ userId, isLead: false, note: note ?? null })
    case 'lawyer':
      return setUserLawyer({ userId, isLawyer: false })
  }
}

export async function setManagementRoyaltyRole(params: {
  userId: string
  role: RoyaltyRole | null
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    rpcClient().rpc<null>('set_management_company_royalty_role', {
      p_target_user_id: params.userId,
      p_royalty_role: params.role,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function setManagementSuperAdmin(params: {
  userId: string
  isSuperAdmin: boolean
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    rpcClient().rpc<null>('set_super_admin', {
      p_target_user_id: params.userId,
      p_is_super_admin: params.isSuperAdmin,
    })
  )
  if (error) throw new Error(formatError(error))
}
